import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { LoadingBlock, ErrorState, Button, toast } from "@/components/ds";
import { Languages as LangIcon, Sparkles } from "lucide-react";

export default function Languages() {
  const qc = useQueryClient();
  const [demo, setDemo] = useState({ text: "Necesito reparar mi aire acondicionado.", out: "" });
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["lang-settings"], queryFn: () => api.langSettings() });
  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const cat = data.supported_catalog as Record<string, string>;
  const entitled: string[] = data.entitled_languages;
  const enabled: string[] = data.enabled_languages;

  const toggle = async (code: string) => {
    const next = enabled.includes(code) ? enabled.filter((c) => c !== code) : [...enabled, code];
    try { await api.langUpdateSettings({ enabled_languages: next }); toast.success("Languages updated"); qc.invalidateQueries({ queryKey: ["lang-settings"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setPrimary = async (code: string) => {
    try { await api.langUpdateSettings({ primary_language: code }); toast.success("Primary language updated"); qc.invalidateQueries({ queryKey: ["lang-settings"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const runDemo = async () => {
    try { const d = await api.langDetect(demo.text); const t = await api.langTranslate(demo.text, "en"); setDemo({ ...demo, out: `Detected: ${d.language} (${Math.round(d.confidence * 100)}%) · EN: ${t.text}` }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="languages-settings">
      <div className="flex items-center gap-2"><LangIcon className="h-6 w-6 text-blue-600" /><h1 className="text-2xl font-semibold text-slate-900">Languages</h1></div>
      <p className="text-sm text-slate-500">ResFil automatically recognizes your customer's language across chat and SMS and continues naturally — no menu, no restart.</p>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Enabled AI Languages</h2>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(cat).map(([code, label]) => {
            const isEntitled = entitled.includes(code);
            const on = enabled.includes(code);
            return (
              <button key={code} disabled={!isEntitled} onClick={() => toggle(code)} data-testid={`lang-toggle-${code}`}
                className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${!isEntitled ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300" : on ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 hover:bg-slate-50"}`}>
                <span>{label} <span className="text-xs text-slate-400">{code}</span></span>
                {!isEntitled ? <span className="text-[10px] uppercase text-slate-400">Upgrade</span> : on ? <span className="text-xs font-medium">On</span> : <span className="text-xs text-slate-400">Off</span>}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex items-center gap-2 text-sm">
          <span className="text-slate-500">Primary:</span>
          <select value={data.primary_language} onChange={(e) => setPrimary(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" data-testid="lang-primary">
            {enabled.map((c) => <option key={c} value={c}>{cat[c]}</option>)}
          </select>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-1 text-sm font-semibold text-slate-800">Voice (Retell)</h2>
        <p className="text-xs text-slate-500">{data.voice_provider?.language_switching_confirmed ? "Multilingual voice enabled." : "Provider Support Required — live multilingual voice needs Retell agent configuration. Text (chat & SMS) is fully multilingual now."}</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-2 flex items-center gap-1 text-sm font-semibold text-slate-800"><Sparkles className="h-4 w-4 text-amber-500" /> Try it</h2>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input value={demo.text} onChange={(e) => setDemo({ ...demo, text: e.target.value })} className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="lang-demo-input" />
          <Button onClick={runDemo} data-testid="lang-demo-run">Detect & Translate</Button>
        </div>
        {demo.out && <p className="mt-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-700" data-testid="lang-demo-out">{demo.out}</p>}
      </div>
    </div>
  );
}
